import { useCallback, useState } from 'react'
import * as React from 'react'
import type { UserProductDesignStore } from '../../UserData/_stores/UserProductDesignStore'
import type { CollectionStore } from '../../UserData/_stores/CollectionStore'
import { useTranslation } from 'react-i18next'
import { Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { MoreVert as MoreVertIcon, LibraryAddRounded as LibraryAddRoundedIcon, PhotoFilterRounded as PhotoFilterRoundedIcon, DriveFileRenameOutlineRounded as DriveFileRenameOutlineRoundedIcon, RemoveCircleOutlineRounded as RemoveCircleOutlineRoundedIcon, AddBusinessRounded as AddBusinessRoundedIcon } from '@mui/icons-material'
import DeleteDesignDialog from './DeleteDesignDialog'
import RenameDesignDialog from './RenameDesignDialog'
import DeleteIcon from '../../Common/Icons/DeleteIcon'
import AddToCollectionDialog from '../Collections/AddToCollectionDialog'
import RemoveFromCollectionDialog from './RemoveFromCollectionDialog'
import OrderDialog from './OrderDialog/OrderDialog'
import PublishProductDesignDialog from '../Stores/AddProductListing/PublishProductDesignDialog'
import ProductDesign from './ProductDesign'
import ProductDesignLinkedStores from './ProductDesignLinkedStores'
import tKey from '../../Common/Utils/tKey'
import type { ProductLayoutType } from '../../UserData/_stores/UserPrefsStore'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import getStoreFeatures from '../../Common/_hooks/Store/getStoreFeatures'

tKey('Order_ctx_verb')

interface Props {
  view: ProductLayoutType
  item: UserProductDesignStore
  selected?: boolean
  onSelect?: (id: number, shiftKey?: boolean) => void
  onUnselect?: (id: number, shiftKey?: boolean) => void
  selectedCollection?: CollectionStore
}

export default function ProductDesignMainActions(props: Props) {
  const [t] = useTranslation(['account-v2', 'account'])
  const [tR] = useTranslation('routes')
  
  const productDesignId = props.item.get('id')
  const isSelectedFromStore = useAppSelector(state => {
    const account = state.get('UIData').get('account')

    if (account.get('productDesignSelectedAllProducts')) {
      return !account.get('productDesignExcludedSelectedProducts').has(productDesignId)
    }

    return account.get('productDesignSelectedProducts').has(productDesignId)
  })
  const isSelected = props.selected ?? isSelectedFromStore

  const [menuOpened, setMenuOpened] = useState(false)
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const [orderDialogOpened, setOrderDialogOpened] = useState(false)
  const [publishProductDesignDialogOpened, setPublishProductDesignDialogOpened] = useState(false)
  const [deleteDialogOpened, setDeleteDialogOpened] = useState(false)
  const [renameDialogOpened, setRenameDialogOpened] = useState(false)
  const [addToCollectionDialogOpened, setAddToCollectionDialogOpened] = useState(false)
  const [removeFromCollectionDialogOpened, setRemoveFromCollectionDialogOpened] = useState(false)
  const hasPublishableStore = useAppSelector(state => state.get('userData').get('stores').some(store => (
    getStoreFeatures(store.get('slug')).canCreateProduct
  )))

  const onEdit = useCallback(() => {
    window.location.href = tR('create') + '?design=' + String(props.item.get('id'))
  }, [])

  const onPhotoLabClick = useCallback(() => {
    setMenuOpened(false)

    const aowWithUi = AOW as typeof AOW & {
      UI: {
        dialog: (options: {
          url: string
          data: Record<string, number>
          success: () => void
        }) => void
      }
    }

    aowWithUi.UI.dialog({
      url: 'account/social-app',
      data: {
        id: props.item.get('id_product_design'),
        product_id: props.item.get('id_product')
      },
      success: () => {
        AOW.SocialApp.init()
      }
    })
  }, [props.item])

  const onOrderClose = useCallback(() => {
    setOrderDialogOpened(false)
  }, [])

  const onOrderOpen = useCallback(() => {
    setMenuOpened(false)
    setOrderDialogOpened(true)
  }, [])

  const onPublishProductDesignDialogClose = useCallback(() => {
    setPublishProductDesignDialogOpened(false)
  }, [])

  const onPublishProductDesignDialogOpen = useCallback(() => {
    setMenuOpened(false)
    setPublishProductDesignDialogOpened(true)
  }, [])

  const onRenameClose = useCallback(() => {
    setRenameDialogOpened(false)
  }, [])

  const onRenameOpen = useCallback(() => {
    setMenuOpened(false)
    setRenameDialogOpened(true)
  }, [])

  const onDeleteClose = useCallback(() => {
    setDeleteDialogOpened(false)
  }, [])

  const onDeleteOpen = useCallback(() => {
    setMenuOpened(false)
    setDeleteDialogOpened(true)
  }, [])

  const onAddToCollectionClose = useCallback(() => {
    setAddToCollectionDialogOpened(false)
  }, [])

  const onAddToCollectionOpen = useCallback(() => {
    setMenuOpened(false)
    setAddToCollectionDialogOpened(true)
  }, [])

  const onRemoveFromCollectionClose = useCallback(() => {
    setRemoveFromCollectionDialogOpened(false)
  }, [])

  const onRemoveFromCollectionOpen = useCallback(() => {
    setMenuOpened(false)
    setRemoveFromCollectionDialogOpened(true)
  }, [])

  const onMenuOpen = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget)
    setMenuOpened(true)
  }, [])

  const onMenuClose = useCallback(() => {
    setMenuOpened(false)
  }, [])

  return <>
    <ProductDesign
      view={props.view}
      item={props.item}
      selected={isSelected}
      onSelect={props.onSelect}
      onUnselect={props.onUnselect}
      caption={props.view === 'list' ? <ProductDesignLinkedStores linkedStores={props.item.get('linkedStores')} /> : undefined}
      imageOverlay={props.view === 'grid' ? <ProductDesignLinkedStores linkedStores={props.item.get('linkedStores')} /> : undefined}
      actions={<>
        <Button
          onClick={onOrderOpen}
          variant="contained"
          color="accent"
        >{t('Order', { context: 'verb' })}</Button>
        <IconButton
          onClick={onMenuOpen}
          size="large">
          <MoreVertIcon />
        </IconButton>
      </>}
    />
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      open={menuOpened}
      onClose={onMenuClose}
    >
      { /* props.item.isEditable() ? <MenuItem onClick={onEdit}>
      <ListItemIcon><DesignServicesIcon /></ListItemIcon>
      <ListItemText>{t('Edit')}</ListItemText>
      </MenuItem> : null */ }
      {hasPublishableStore ? <MenuItem
        onClick={onPublishProductDesignDialogOpen}
        sx={{
          color: 'primary.main',
          '& .MuiListItemIcon-root': {
            color: 'primary.main',
          },
        }}
      >
        <ListItemIcon><AddBusinessRoundedIcon /></ListItemIcon>
        <ListItemText>{t('Add to store')}</ListItemText>
      </MenuItem> : null}
      <MenuItem 
        onClick={onAddToCollectionOpen} 
        sx={{
          color: 'secondary.main',
          '& .MuiListItemIcon-root': {
            color: 'secondary.main',
          },
        }}
      >
        <ListItemIcon><LibraryAddRoundedIcon /></ListItemIcon>
        <ListItemText>{t('Add to collection')}</ListItemText>
      </MenuItem>
      {props.selectedCollection ? <MenuItem 
        onClick={onRemoveFromCollectionOpen} 
        sx={{
          color: 'secondary.main',
          '& .MuiListItemIcon-root': {
            color: 'secondary.main',
          },
        }}
      >
        <ListItemIcon><RemoveCircleOutlineRoundedIcon /></ListItemIcon>
        <ListItemText>{t('Remove from collection')}</ListItemText>
      </MenuItem> : null}
      <MenuItem onClick={onRenameOpen}>
        <ListItemIcon><DriveFileRenameOutlineRoundedIcon /></ListItemIcon>
        <ListItemText>{t('Rename')}</ListItemText>
      </MenuItem>
      <MenuItem 
        onClick={onPhotoLabClick} 
        sx={{
          display: {
            xs: 'none',
            md: 'flex',
          }
        }}
      >
        <ListItemIcon><PhotoFilterRoundedIcon /></ListItemIcon>
        <ListItemText>{t('Photo Lab')}</ListItemText>
      </MenuItem>
      <MenuItem 
        onClick={onDeleteOpen} 
        sx={{
          color: 'error.main',
          '& .MuiListItemIcon-root': {
            color: 'error.main',
          },
        }}
      >
        <ListItemIcon><DeleteIcon /></ListItemIcon>
        <ListItemText>{t('Delete')}</ListItemText>
      </MenuItem>
    </Menu>

    <OrderDialog
      opened={orderDialogOpened}
      productDesignId={props.item.get('id')}
      onClose={onOrderClose}
    />

    <PublishProductDesignDialog
      opened={publishProductDesignDialogOpened}
      productDesignId={props.item.get('id')}
      onClose={onPublishProductDesignDialogClose}
    />

    <RenameDesignDialog
      productDesign={props.item}
      opened={renameDialogOpened}
      onClose={onRenameClose}
    />

    <DeleteDesignDialog
      productDesign={props.item}
      opened={deleteDialogOpened}
      onClose={onDeleteClose}
    />

    <AddToCollectionDialog
      productDesign={props.item}
      opened={addToCollectionDialogOpened}
      onClose={onAddToCollectionClose}
    />

    {props.selectedCollection ? <RemoveFromCollectionDialog
      collectionId={props.selectedCollection.get('id')}
      collectionName={props.selectedCollection.get('name')}
      productDesign={props.item}
      opened={removeFromCollectionDialogOpened}
      onClose={onRemoveFromCollectionClose}
    /> : null}
  </>
}
